import { format } from 'date-fns';

export interface CalendarEventParams {
  title: string;
  description?: string;
  startDate: Date;
  endDate?: Date | null;
  isAllDay?: boolean;
  location?: string;
  url?: string;
}

type ICSEventParams = CalendarEventParams;

function formatICSDate(date: Date, allDay: boolean): string {
  if (allDay) {
    return format(date, "yyyyMMdd");
  }
  return format(date, "yyyyMMdd'T'HHmmss");
}

function escapeICSText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

export function generateICSContent(event: ICSEventParams): string {
  const { title, description, startDate, endDate, isAllDay = false, location, url } = event;

  const dtStart = isAllDay
    ? `DTSTART;VALUE=DATE:${formatICSDate(startDate, true)}`
    : `DTSTART:${formatICSDate(startDate, false)}`;

  let dtEnd = '';
  if (endDate) {
    dtEnd = isAllDay
      ? `DTEND;VALUE=DATE:${formatICSDate(endDate, true)}`
      : `DTEND:${formatICSDate(endDate, false)}`;
  } else if (isAllDay) {
    const nextDay = new Date(startDate);
    nextDay.setDate(nextDay.getDate() + 1);
    dtEnd = `DTEND;VALUE=DATE:${formatICSDate(nextDay, true)}`;
  }

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ORBCoN//Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${Date.now()}-${Math.random().toString(36).slice(2)}@orbcon.ca`,
    dtStart,
    ...(dtEnd ? [dtEnd] : []),
    `SUMMARY:${escapeICSText(title)}`,
    ...(description ? [`DESCRIPTION:${escapeICSText(description)}`] : []),
    ...(location ? [`LOCATION:${escapeICSText(location)}`] : []),
    ...(url ? [`URL:${url}`] : []),
    `DTSTAMP:${formatICSDate(new Date(), false)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return lines.join('\r\n');
}

export function downloadICSFile(event: ICSEventParams): void {
  const content = generateICSContent(event);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50)}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatGoogleDate(date: Date, allDay: boolean): string {
  if (allDay) return format(date, "yyyyMMdd");
  return format(date, "yyyyMMdd'T'HHmmss");
}

export function getGoogleCalendarUrl(event: CalendarEventParams): string {
  const { title, description, startDate, endDate, isAllDay = false, location } = event;
  const params = new URLSearchParams();
  params.set('action', 'TEMPLATE');
  params.set('text', title);

  const start = formatGoogleDate(startDate, isAllDay);
  let end: string;
  if (endDate) {
    end = formatGoogleDate(endDate, isAllDay);
  } else if (isAllDay) {
    const nextDay = new Date(startDate);
    nextDay.setDate(nextDay.getDate() + 1);
    end = formatGoogleDate(nextDay, true);
  } else {
    end = start;
  }
  params.set('dates', `${start}/${end}`);

  if (description) params.set('details', description);
  if (location) params.set('location', location);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function getOutlookCalendarUrl(event: CalendarEventParams): string {
  const { title, description, startDate, endDate, isAllDay = false, location } = event;
  const params = new URLSearchParams();
  params.set('rru', 'addevent');
  params.set('subject', title);
  params.set('startdt', startDate.toISOString());
  if (endDate) params.set('enddt', endDate.toISOString());
  if (isAllDay) params.set('allday', 'true');
  if (description) params.set('body', description);
  if (location) params.set('location', location);

  return `https://outlook.office.com/calendar/0/action/compose?${params.toString()}`;
}
